import type { ReactNode } from 'react';
import { colorIsLight } from '@/utils/drill-ui';
import { cn } from '@/lib/utils';

const SIZE_CLASSES = {
  sm: 'px-1.5 py-0 text-[10px]',
  md: 'px-2.5 py-0.5 text-xs',
} as const;

interface TagPillProps {
  /** Solid, data-driven background color (hex). Text color is chosen for contrast. */
  color: string;
  size?: keyof typeof SIZE_CLASSES;
  title?: string;
  'aria-label'?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Shared rounded tag pill with a dynamic (taxonomy/practitioner-chosen) background
 * and contrast-aware text. Consolidates the classification + custom-tag badge chrome
 * that was duplicated across components. The color is intentionally inline (a data
 * value, chart-legend-like), not a theme token — see theme-coverage-allowlist.yaml.
 */
export function TagPill({
  color,
  size = 'md',
  title,
  'aria-label': ariaLabel,
  className,
  children,
}: TagPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        SIZE_CLASSES[size],
        colorIsLight(color) ? 'text-foreground' : 'text-white',
        className
      )}
      style={{ backgroundColor: color }}
      title={title}
      aria-label={ariaLabel}
    >
      {children}
    </span>
  );
}
